"use client";
import React, { useState, useEffect } from "react";
import {
  Edit, Trash2, Search, X, Eye, ChevronsLeft, ChevronsRight,
} from "lucide-react";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { useProjects } from "../../context/ProjectContext";
import { BASE_URL, CDN_URL } from "../../config/apiConfig";
import { useAuth } from "../../context/AuthContext";

// ── Status config ──────────────────────────────────────────────────────────────
const STATUS_LINES = [
  { key: "Cancelled",   color: "#ef4444", group: "terminal" },
  { key: "Completed",   color: "#22c55e", group: "terminal" },
  { key: "Designing",   color: "#a78bfa", group: "active"   },
  { key: "Finalizing",  color: "#14b8a6", group: "active"   },
  { key: "In Progress", color: "#3b82f6", group: "active"   },
  { key: "OnHold",      color: "#9ca3af", group: "paused"   },
  { key: "Pending",     color: "#facc15", group: "paused"   },
  { key: "Planning",    color: "#818cf8", group: "active"   },
  { key: "Reviewing",   color: "#f97316", group: "active"   },
  { key: "Started",     color: "#38bdf8", group: "active"   },
];

const MONTHS = ["All","Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export default function Projects() {
  const {
    projects, addProject, updateProject, deleteProject,
    clients, projectTypes, assignEmployeesToProject,
  } = useProjects();

  // ── UI state ─────────────────────────────────────────────────────────────────
  const [searchTerm,   setSearchTerm]   = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [showModal,    setShowModal]    = useState(false);
  const [editModal,    setEditModal]    = useState(null);
  const [deleteModal,  setDeleteModal]  = useState(null);
  const [viewModal,    setViewModal]    = useState(null);
  const [assignModal,  setAssignModal]  = useState(null);
  const [page,         setPage]         = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // ── Chart filter state ────────────────────────────────────────────────────────
  const [selectedMonth, setSelectedMonth] = useState("All");
  const [selectedGroup, setSelectedGroup] = useState("all");

  // ── Data state ────────────────────────────────────────────────────────────────
  const [chartData,     setChartData]     = useState([]);
  const [employees,     setEmployees]     = useState([]);
  const [projectStats,  setProjectStats]  = useState({
    completed: { percent: "0%", count: "0 Projects" },
    pending:   { percent: "0%", count: "0 Projects" },
    onhold:    { percent: "0%", count: "0 Projects" },
  });

  const { canDo } = useAuth();

  // ── Active project types (used in Add / Edit dropdowns) ───────────────────────
  const activeProjectTypes = projectTypes.filter(
    (t) => t.status?.toLowerCase() === "active"
  );

  // ── Fetch employees ───────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchEmployees = async () => {
      const token = localStorage.getItem("token");
      const res  = await fetch(`${BASE_URL}/employees`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setEmployees(data.data);
    };
    fetchEmployees();
  }, []);

  // ── Fetch assigned employees when assignModal opens ───────────────────────────
  useEffect(() => {
    const fetchAssignedEmployees = async () => {
      if (!assignModal) return;
      try {
        const token = localStorage.getItem("token");
        const res   = await fetch(`${BASE_URL}/projects/${assignModal.id}/employees`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Failed to fetch assigned employees");
        const result     = await res.json();
        const assignedIds = result.data.map((emp) => emp.id);
        setAssignModal((prev) => ({ ...prev, assigned_to: assignedIds }));
      } catch (err) {
        console.error("Error fetching assigned employees:", err);
      }
    };
    fetchAssignedEmployees();
  }, [assignModal?.id]);

  // ── Fetch chart data ──────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchChartData = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const response = await fetch(`${BASE_URL}/project_status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const result  = await response.json();
        const apiData = result?.data || [];
        const months  = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

        const formattedData = months.map((month) => {
          const found = apiData.find((item) => item.month === month);
          return {
            name:          month,
            Pending:       found ? parseInt(found.Pending       || 0) : 0,
            Started:       found ? parseInt(found.Started       || 0) : 0,
            Planning:      found ? parseInt(found.Planning      || 0) : 0,
            Designing:     found ? parseInt(found.Designing     || 0) : 0,
            "In Progress": found ? parseInt(found["In Progress"]|| 0) : 0,
            Reviewing:     found ? parseInt(found.Reviewing     || 0) : 0,
            Finalizing:    found ? parseInt(found.Finalizing    || 0) : 0,
            OnHold:        found ? parseInt(found["On Hold"]    || 0) : 0,
            Completed:     found ? parseInt(found.Completed     || 0) : 0,
            Cancelled:     found ? parseInt(found.Cancelled     || 0) : 0,
          };
        });
        setChartData(formattedData);
      } catch (error) {
        console.error("Error fetching chart data:", error);
      }
    };
    fetchChartData();
  }, []);

  // ── Project stats ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const total          = projects.length;
    const completedCount = projects.filter((p) => p.status?.toLowerCase() === "completed").length;
    const pendingCount   = projects.filter((p) =>
      ["pending","started","planning","designing","in progress","reviewing","finalizing"]
        .includes(p.status?.toLowerCase())
    ).length;
    const onholdCount = projects.filter((p) => p.status?.toLowerCase() === "on hold").length;
    const pct = (n) => (total > 0 ? Math.round((n / total) * 100) + "%" : "0%");

    setProjectStats({
      completed: { percent: pct(completedCount), count: completedCount + " Projects" },
      pending:   { percent: pct(pendingCount),   count: pendingCount   + " Projects" },
      onhold:    { percent: pct(onholdCount),     count: onholdCount   + " Projects" },
    });
  }, [projects]);

  // ── Derived chart data ────────────────────────────────────────────────────────
  const filteredChartData = selectedMonth === "All"
    ? chartData
    : chartData.filter((d) => d.name === selectedMonth);

  const visibleLines = selectedGroup === "all"
    ? STATUS_LINES
    : STATUS_LINES.filter((s) => s.group === selectedGroup);

  // ── Helpers ───────────────────────────────────────────────────────────────────
  const totalPages = Math.ceil(projects.length / itemsPerPage);

  const getStatusColor = (status) => {
    switch (status) {
      case "Pending":     return "bg-yellow-100 text-yellow-700";
      case "Started":     return "bg-sky-100 text-sky-700";
      case "Planning":    return "bg-indigo-100 text-indigo-700";
      case "Designing":   return "bg-purple-100 text-purple-700";
      case "In Progress": return "bg-blue-100 text-blue-700";
      case "Reviewing":   return "bg-orange-100 text-orange-700";
      case "Finalizing":  return "bg-teal-100 text-teal-700";
      case "On Hold":     return "bg-gray-200 text-gray-700";
      case "Completed":   return "bg-green-100 text-green-700";
      case "Cancelled":   return "bg-red-100 text-red-700";
      default:            return "bg-slate-100 text-slate-700";
    }
  };

  // ── Handlers ──────────────────────────────────────────────────────────────────
  const handleAddProject = async (e) => {
    e.preventDefault();
    const el = e.target.elements;
    const payload = {
      project_name:    el.project_name.value,
      client_id:       el.client_id.value,
      project_type_id: el.project_type_id.value,
      start_date:      el.start_date.value,
      end_date:        el.end_date.value,
      email:           el.email.value,
      phone:           el.phone.value,
      budget:          el.budget.value,
      status:          el.status.value || "pending",
      description:     el.description.value,
      note:            el.note.value,
    };
    const response = await addProject(payload);
    if (response?.status === 201) { setShowModal(false); e.target.reset(); }
    else alert(response.message || "Failed to add project.");
  };

  const handleUpdateProject = async (e) => {
    e.preventDefault();
    const payload = {
      project_name:    editModal.project_name,
      client_id:       editModal.client_id,
      project_type_id: editModal.project_type_id,
      start_date:      editModal.start_date,
      end_date:        editModal.end_date,
      status:          editModal.status,
      description:     editModal.description,
      budget:          editModal.budget,
    };
    const success = await updateProject(editModal.id, payload);
    if (success) setEditModal(null);
  };

  const handleConfirmDelete = async () => {
    if (deleteModal) { await deleteProject(deleteModal.id); setDeleteModal(null); }
  };

  const handleAssign = async () => {
    if (!assignModal.assigned_to?.length) { alert("Please select at least 1 employee!"); return; }
    const result = await assignEmployeesToProject(assignModal.id, assignModal.assigned_to);
    if (result?.status === 200) { alert("Project Assigned Successfully!"); setAssignModal(null); }
    else alert(result?.message || "Failed to assign project!");
  };

  const filteredProjects = projects.filter((proj) => {
    const matchesSearch =
      proj.id?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
      proj.project_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      proj.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      proj.status?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === "All" || proj.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="bg-sky-50">
      <div className="px-6 py-6 max-sm:px-4">

        {/* Page title */}
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-5">Projects</h2>

        {/* ── Project Status Chart Card ─────────────────────────────────────── */}
        <div className="bg-white rounded-2xl p-5 shadow-md mb-8">

          {/* Card header with filters */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Project Status</h3>

            <div className="flex flex-wrap items-center gap-2">
              {/* Month filter */}
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 outline-none shadow-sm bg-white focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition"
              >
                {MONTHS.map((m) => (
                  <option key={m} value={m}>{m === "All" ? "All Months" : m}</option>
                ))}
              </select>

              {/* Group filter */}
              <select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 outline-none shadow-sm bg-white focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="terminal">Completed / Cancelled</option>
              </select>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-6">

            {/* Line Chart */}
            <div className="w-full lg:w-3/4 h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={filteredChartData}
                  margin={{ top: 5, right: 20, left: 0, bottom: 55 }}
                >
                  <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: "#9ca3af" }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 12, fill: "#9ca3af" }}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      borderRadius: "8px",
                      border: "1px solid #e5e7eb",
                      fontSize: "12px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                    }}
                    wrapperStyle={{ zIndex: 9999 }}
                  />
                  <Legend
                    wrapperStyle={{ fontSize: "11px", paddingTop: "10px", bottom: 0 }}
                    iconType="circle"
                    iconSize={7}
                  />
                  {visibleLines.map(({ key, color }) => (
                    <Line
                      key={key}
                      type="monotone"
                      dataKey={key}
                      stroke={color}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Side stat cards */}
            <div className="flex flex-col justify-start gap-4 lg:w-1/4">
              {[
                { color: "green",  title: "Completed Projects", ...projectStats.completed },
                { color: "yellow", title: "Pending Projects",   ...projectStats.pending   },
                { color: "red",    title: "Onhold Projects",    ...projectStats.onhold    },
              ].map((item, i) => (
                <div
                  key={i}
                  className={`flex items-center border border-${item.color}-200 p-4 rounded-xl shadow-sm w-full`}
                >
                  <span className={`w-4 h-4 bg-${item.color}-500 rounded-full mr-3 mt-1`} />
                  <div className="flex flex-col w-full">
                    <h4 className="text-sm font-semibold text-gray-700">{item.title}</h4>
                    <div className="h-[1px] bg-gray-300 my-2 w-full" />
                    <div className="flex justify-between items-center">
                      <p className={`text-xs text-${item.color}-500 bg-${item.color}-50 p-1 rounded-xl`}>
                        {item.percent}
                      </p>
                      <span className="text-gray-700 text-sm font-medium">{item.count}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Table toolbar ─────────────────────────────────────────────────── */}
        <div className="flex flex-wrap items-center justify-between mb-6 bg-white px-6 py-4 rounded-xl shadow-sm">
          <h1 className="text-3xl font-bold text-gray-800 max-sm:text-2xl whitespace-nowrap">
            Projects
          </h1>

          <div className="flex items-center gap-3 ml-auto flex-col sm:flex-row w-full sm:w-auto">
            <div className="flex items-center shadow-md border border-gray-200 rounded-lg px-2 py-1 w-full sm:w-72 md:w-80 focus-within:ring-2 focus-within:ring-[#8c57ff]/40 focus-within:border-[#8c57ff] transition">
              <select
                className="bg-transparent text-gray-700 px-2 py-1 outline-none border-r border-gray-300"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                {["All","Pending","Started","Planning","Designing","In Progress",
                  "Reviewing","Finalizing","On Hold","Completed","Cancelled"].map((s) => (
                  <option key={s}>{s}</option>
                ))}
              </select>
              <div className="relative flex-1 ml-2">
                <Search className="absolute left-3 top-2.5 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 bg-transparent outline-none text-gray-700"
                />
              </div>
            </div>

            {canDo("Projects", "Add") && (
              <button
                onClick={() => setShowModal(true)}
                className="text-white px-4 py-2 rounded-lg shadow transition-all w-full sm:w-auto"
                style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }}
              >
                Create New Project
              </button>
            )}
          </div>
        </div>

        {/* ── Projects Table ────────────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl p-5 shadow-md">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left border-collapse">
            <thead>
              <tr className="bg-indigo-50 text-gray-700">
                {["ID","Project","Type","Client","Mobile","Start","End","Budget","Status","Assign","Team Members"].map((h) => (
                  <th key={h} className="py-3 px-4 font-semibold">{h}</th>
                ))}
                {(canDo("Projects","Edit") || canDo("Projects","View") || canDo("Projects","Delete")) && (
                  <th className="py-3 px-4 font-semibold text-center">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {filteredProjects
                .slice((page - 1) * itemsPerPage, page * itemsPerPage)
                .map((proj, index) => (
                  <tr key={index} className="border-t hover:bg-gray-50 transition-all">
                    <td className="py-3 px-4 text-indigo-600">{proj.id}</td>
                    <td className="py-3 px-4">{proj.project_name}</td>
                    <td className="py-3 px-4 whitespace-nowrap">{proj.project_type_name}</td>
                    <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                      {proj.client_first_name} {proj.client_last_name}
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">{proj.phone}</td>
                    <td className="py-3 px-4 whitespace-nowrap">{proj.start_date}</td>
                    <td className="py-3 px-4 whitespace-nowrap">{proj.end_date}</td>
                    <td className="py-3 px-4 whitespace-nowrap">₹ {proj.budget}</td>
                    <td className="py-3 px-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(proj.status)}`}>
                        {proj.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <button
                        className="text-purple-600 hover:text-purple-800 text-xs underline"
                        onClick={() => setAssignModal(proj)}
                      >
                        Assign
                      </button>
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap">
                      <div className="flex -space-x-2">
                        {proj.assigned_users?.slice(0, 3).map((user, idx) => (
                          <img
                            key={idx}
                            src={user.image ? `${CDN_URL}/${user.image}` : "/images/default.jpg"}
                            className="w-8 h-8 rounded-full border-2 border-white"
                            alt={user.first_name}
                          />
                        ))}
                        {proj.assigned_users?.length > 3 && (
                          <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-700 text-xs flex items-center justify-center border-2 border-white">
                            +{proj.assigned_users.length - 3}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 flex space-x-3 text-gray-600 whitespace-nowrap">
                      {canDo("Projects","View") && (
                        <Eye className="h-4 w-4 cursor-pointer hover:text-blue-600" onClick={() => setViewModal(proj)} />
                      )}
                      {canDo("Projects","Edit") && (
                        <Edit className="h-4 w-4 cursor-pointer hover:text-sky-600" onClick={() => setEditModal(proj)} />
                      )}
                      {canDo("Projects","Delete") && (
                        <Trash2 className="h-4 w-4 cursor-pointer hover:text-red-600" onClick={() => setDeleteModal(proj)} />
                      )}
                    </td>
                  </tr>
                ))}
              {filteredProjects.length === 0 && (
                <tr>
                  <td colSpan="12" className="py-4 text-center text-gray-500">No projects found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ───────────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-3">
          <div className="flex items-center space-x-2">
            <label className="text-gray-700 text-sm">Rows:</label>
            <select
              className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition"
              value={itemsPerPage}
              onChange={(e) => { setPage(1); setItemsPerPage(Number(e.target.value)); }}
            >
              {[3, 5, 10, 20].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>

          <div className="flex items-center space-x-1">
            <button onClick={() => setPage(1)} disabled={page === 1}
              className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <ChevronsLeft size={20} />
            </button>
            <button onClick={() => setPage((p) => Math.max(p - 1, 1))} disabled={page === 1}
              className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <MdKeyboardArrowLeft size={20} />
            </button>

            {(() => {
              const pages = [];
              let start = Math.max(page - 1, 1);
              let end   = Math.min(start + 2, totalPages);
              if (end - start < 2) start = Math.max(end - 2, 1);
              for (let i = start; i <= end; i++) {
                pages.push(
                  <button key={i} onClick={() => setPage(i)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${i === page ? "text-white bg-[linear-gradient(135deg,#2563eb,#d12465)]" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                    {i}
                  </button>
                );
              }
              return pages;
            })()}

            <button onClick={() => setPage((p) => Math.min(p + 1, totalPages))} disabled={page === totalPages}
              className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <MdKeyboardArrowRight size={20} />
            </button>
            <button onClick={() => setPage(totalPages)} disabled={page === totalPages}
              className="px-2.5 py-1.5 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <ChevronsRight size={20} />
            </button>
          </div>
        </div>
        </div>
      </div>

      {/* ── Add Project Modal ──────────────────────────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl p-6 relative overflow-y-auto max-h-[90vh]">
            <button onClick={() => setShowModal(false)} className="absolute right-4 top-4 text-gray-500 hover:text-gray-700">
              <X className="h-5 w-5" />
            </button>
            <h3 className="text-xl font-semibold mb-4">Add New Project</h3>
            <form className="space-y-4" onSubmit={handleAddProject}>
              <div>
                <label className="block text-sm font-medium mb-1">Client Name</label>
                <select className="border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition" name="client_id" defaultValue="">
                  <option value="" disabled>Select Client</option>
                 {clients.filter((c) => c.status?.toLowerCase() === "active").map((c) => (
  <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>
))}
                </select>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Project Name</label>
                  <input required type="text" name="project_name" placeholder="Enter Project Name" className="border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Project Type</label>
                  <select name="project_type_id" required className="border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition" defaultValue="">
                    <option value="" disabled>Select Project Type</option>
                    {activeProjectTypes.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Start Date</label>
                  <input type="date" name="start_date" required className="border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">End Date</label>
                  <input type="date" name="end_date" className="border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <input type="email" name="email" placeholder="Enter Email" className="border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Phone Number</label>
                  <input type="tel" name="phone" placeholder="Enter Phone Number" maxLength={10}
                    pattern="[0-9]{10}" inputMode="numeric"
                    onInput={(e) => { e.target.value = e.target.value.replace(/\D/g, ""); }}
                    className="border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition" required />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Budget</label>
                  <input type="number" name="budget" placeholder="e.g. 25000" className="border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition" min="0"
                    onInput={(e) => { if (e.target.value < 0) e.target.value = 0; }} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Status</label>
                  <select name="status" className="border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition">
                    {["Pending","Started","Planning","Designing","In Progress","Reviewing","Finalizing","On Hold","Completed","Cancelled"].map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea placeholder="Enter description..." rows="2" name="description" className="border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Note</label>
                <textarea placeholder="Enter additional notes..." rows="2" name="note" className="border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition" />
              </div>

              <div className="flex justify-end space-x-3 mt-4">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 rounded-lg border">Cancel</button>
                <button type="submit" className="px-4 py-2 rounded-lg text-white"
                  style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }}>
                  Create New Project
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Edit Project Modal ─────────────────────────────────────────────── */}
      {editModal && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl p-6 relative overflow-y-auto max-h-[90vh]">
            <button onClick={() => setEditModal(null)} className="absolute right-4 top-4 text-gray-500 hover:text-gray-700">
              <X className="h-5 w-5" />
            </button>
            <h3 className="text-xl font-semibold mb-4">Edit Project</h3>
            <form className="space-y-4" onSubmit={handleUpdateProject}>
              <div>
                <label className="block text-sm font-medium mb-1">Project Name</label>
                <input type="text" value={editModal.project_name || ""} onChange={(e) => setEditModal((p) => ({ ...p, project_name: e.target.value }))} className="border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Project Type</label>
                <select
                  value={editModal.project_type_id || ""}
                  onChange={(e) => setEditModal((p) => ({ ...p, project_type_id: e.target.value }))}
                  className="border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition"
                >
                  <option value="" disabled>Select Project Type</option>
                  {projectTypes
                    .filter(
                      (t) =>
                        t.status?.toLowerCase() === "active" ||
                        String(t.id) === String(editModal.project_type_id)
                    )
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                        {t.status?.toLowerCase() !== "active" ? " (Inactive)" : ""}
                      </option>
                    ))}
                </select>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Start Date</label>
                  <input type="date" value={editModal.start_date || ""} onChange={(e) => setEditModal((p) => ({ ...p, start_date: e.target.value }))} className="border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">End Date</label>
                  <input type="date" value={editModal.end_date || ""} onChange={(e) => setEditModal((p) => ({ ...p, end_date: e.target.value }))} className="border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <input type="email" value={editModal.email || ""} onChange={(e) => setEditModal((p) => ({ ...p, email: e.target.value }))} className="border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Phone Number</label>
                  <input type="tel" value={editModal.phone || ""} inputMode="numeric" placeholder="Enter Phone Number"
                    onChange={(e) => { let v = e.target.value.replace(/\D/g,""); if (v.length>10) v=v.slice(0,10); setEditModal((p) => ({ ...p, phone: v })); }}
                    className="border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition" />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Budget</label>
                  <input type="text" value={editModal.budget || ""} inputMode="numeric" placeholder="Enter Budget"
                    onChange={(e) => { const v = e.target.value.replace(/\D/g,""); setEditModal((p) => ({ ...p, budget: v })); }}
                    className="border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Status</label>
                  <select value={editModal.status || "Pending"} onChange={(e) => setEditModal((p) => ({ ...p, status: e.target.value }))} className="border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition">
                    {["Pending","Started","Planning","Designing","In Progress","Reviewing","Finalizing","On Hold","Completed","Cancelled"].map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea rows="2" value={editModal.description || ""} onChange={(e) => setEditModal((p) => ({ ...p, description: e.target.value }))} className="border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Note</label>
                <textarea rows="2" value={editModal.note || ""} onChange={(e) => setEditModal((p) => ({ ...p, note: e.target.value }))} className="border rounded-lg px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition" />
              </div>

              <div className="flex justify-end space-x-3 mt-4">
                <button type="button" onClick={() => setEditModal(null)} className="px-4 py-2 rounded-lg border">Cancel</button>
                <button type="submit" className="px-4 py-2 rounded-lg text-white"
                  style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }}>
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Delete Modal ───────────────────────────────────────────────────── */}
      {deleteModal && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-6 relative text-center">
            <X className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 cursor-pointer" onClick={() => setDeleteModal(null)} />
            <Trash2 className="mx-auto text-red-500 mb-4" size={40} />
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Confirm Deletion</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete project{" "}
              <span className="font-semibold text-red-500">{deleteModal.project_name}</span>?
              This action cannot be undone.
            </p>
            <div className="flex justify-center gap-4">
              <button onClick={() => setDeleteModal(null)} className="px-4 py-2 rounded-lg border">Cancel</button>
              <button onClick={handleConfirmDelete} className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ── View Modal ────────────────────────────────────────────────────── */}
      {viewModal && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-6 relative">
            <button onClick={() => setViewModal(null)} className="absolute right-4 top-4 text-gray-500 hover:text-gray-700">
              <X className="h-5 w-5" />
            </button>
            <h3 className="text-xl font-semibold mb-4">Project Details</h3>
            <div className="space-y-2 text-left">
              <p><strong>ID:</strong> {viewModal.id}</p>
              <p><strong>Name:</strong> {viewModal.project_name}</p>
              <p><strong>Type:</strong> {viewModal.project_type_name}</p>
              <p><strong>Client:</strong> {viewModal.client_first_name} {viewModal.client_last_name}</p>
              <p><strong>Start:</strong> {viewModal.start_date}</p>
              <p><strong>End:</strong> {viewModal.end_date}</p>
              <p><strong>Budget:</strong> {viewModal.budget}</p>
              <p><strong>Status:</strong> {viewModal.status}</p>
            </div>
            <div className="flex justify-end mt-4">
              <button onClick={() => setViewModal(null)} className="px-4 py-2 rounded-lg text-white"
                style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Assign Modal ──────────────────────────────────────────────────── */}
      {assignModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-lg p-6 relative shadow-xl">
            <button className="absolute right-4 top-4 text-gray-500 hover:text-gray-700" onClick={() => setAssignModal(null)}>
              <X size={20} />
            </button>
            <h2 className="text-xl font-semibold mb-4">
              Assign Employees to: <span className="text-purple-600">{assignModal.project_name}</span>
            </h2>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Select Employees</label>
              <select multiple className="border rounded-lg px-3 py-2 w-full h-32 focus:outline-none focus:ring-2 focus:ring-[#8c57ff]/40 focus:border-[#8c57ff] transition"
                value={assignModal.assigned_to || []}
                onChange={(e) => setAssignModal({
                  ...assignModal,
                  assigned_to: Array.from(e.target.selectedOptions, (o) => Number(o.value)),
                })}
              >
                {employees?.filter((e) => e.status === "active").map((emp) => (
                  <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end">
              <button onClick={handleAssign} className="px-4 py-2 text-white rounded-lg"
                style={{ background: "linear-gradient(135deg, #2563eb, #d12465)" }}>
                Assign
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}